import {api} from './api';
import { Program, ProgramUser } from '../model/program';
import { Course } from '../model/course';
import { User } from '../model/user';

export const getPrograms = async (accessToken: string): Promise<Program[]> => {
    const response = await api.get<Program[]>(`/programs`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};

export const getProgram = async (accessToken: string, uuid: string): Promise<Program> => {
    const response = await api.get<Program>(`/programs/${uuid}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};

export const putProgram = async (accessToken: string, program: Program): Promise<Program> => {
    const response = await api.put<Program>(`/programs`, 
        program, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};

export const cloneProgram = async (accessToken: string, clientName: string, program: Program): Promise<Program> => {
    const response = await api.put<Program>(`/programs?clone&clientName=${clientName}&programId=${program.id}`, 
        null, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};

export const getProgramCourses = async (accessToken: string, uuid: string): Promise<Course[]> => {
    const response = await api.get<Course[]>(`/programs/${uuid}/courses`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};

export const deleteProgram = async (accessToken: string, programId: string): Promise<boolean> => {
    const response = await api.delete<boolean>(`/programs/${programId}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    return response.status == 204 || response.status == 200
};

export const getProgramUsers = async (accessToken: string, programId: string): Promise<ProgramUser[]> => {
    const response = await api.get(`/programs/${programId}/users`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    return response.data
}

export const getProgramsUsers = async (accessToken: string, programIds: string[]): Promise<ProgramUser[]> => {
    const programUserPromises = programIds.map(id => getProgramUsers(accessToken, id));

    const response = await Promise.allSettled(programUserPromises);

    let programUsers: ProgramUser[] = []

    for (const result of response) {
        if (result.status === 'fulfilled') {
            programUsers.push(...result.value)
        }
    }

    return programUsers
}

export const addUsersToProgram = async (accessToken: string, program: Program, users: User[]): Promise<ProgramUser[]> => {
    let ids = users.map(user => user.id)

    const response = await api.post<ProgramUser[]>(`/programs/${program.id}/users`, 
        ids, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const userMap = new Map(users.map(u => [u.id, u]));
    return response.data.map(pu => {
        const user = userMap.get(pu.userId);
        return {
            ...pu,
            firstName: pu.firstName || user?.firstName,
            lastName: pu.lastName || user?.lastName,
            department: pu.department || user?.department,
        };
    });
};

export const removeUserFromProgram = async (accessToken: string, programId: string, userId: string): Promise<boolean> => {
    const response = await api.delete<boolean>(`/programs/${programId}/users/${userId}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return response.data;
};